import type { AzDanceBookingSummaryParticipantDto } from "@/api/api-client/service";
import type { RegisteredParticipant } from "@/state/stores/registrationStore";
import type { CartItem } from "@/state/stores/activityStore";
import {
    lookingForPartnerForBookingApi,
    mapContactTypeToApi,
    mapContactValueForApi,
    mapCountryToCode,
    mapGenderToApi,
    mapLevelToApi,
    mapRoleToApi,
    parseOptionalAge,
    registrationModeForApi,
} from "@/lib/mapRegisteredParticipantToAzdanceApi";
import { expandChampionshipCopartnerForUpsert } from "@/components/design/pages/registration-flow-page/registrationFlowMappers";
import {
    dedupeEphemeralRegisteredParticipantsForDraft,
    dedupeRegisteredParticipantsById,
} from "@/lib/registeredParticipantDedupe";
import { participantIdsInGuestMode } from "@/api/aztangofest/participantGuestIds";

const SERVER_ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

function copartnerIdForDraftBatch(
    p: RegisteredParticipant,
    all: RegisteredParticipant[],
    selfIndex: number,
    guestIds: Set<string>,
): string | null | undefined {
    if (p.isChampionshipParticipant !== true || p.isSolo === true) {
        return "";
    }
    const link = p.partnerId?.trim();
    if (!link) return undefined;
    /** Индекс в массиве запрещён при patch; если уже есть ULID — только он. */
    if (SERVER_ULID_RE.test(link)) {
        if (guestIds.has(link)) return "";
        return link;
    }
    const idx = all.findIndex((x) => x.id === link);
    if (idx >= 0 && idx !== selfIndex) {
        const oid = all[idx]!.id?.trim();
        if (oid && SERVER_ULID_RE.test(oid)) {
            if (guestIds.has(oid)) return "";
            return oid;
        }
        return String(idx);
    }
    return undefined;
}

function mapParticipantToUpsertPersonal(
    p: RegisteredParticipant,
    summarySp: AzDanceBookingSummaryParticipantDto | undefined,
    guestIds: Set<string>,
): Record<string, unknown> {
    const wantsCopartnerLink =
        p.isChampionshipParticipant === true && p.isSolo !== true;

    const copartnerFromSummary =
        wantsCopartnerLink &&
        summarySp &&
        typeof (summarySp as { copartnerId?: string }).copartnerId === "string"
            ? (summarySp as { copartnerId?: string }).copartnerId?.trim()
            : undefined;
    const partnerLink = wantsCopartnerLink ? p.partnerId?.trim() : "";
    let copartnerIdInitial: string | undefined = !wantsCopartnerLink
        ? ""
        : partnerLink && SERVER_ULID_RE.test(partnerLink)
          ? partnerLink
          : copartnerFromSummary &&
              SERVER_ULID_RE.test(copartnerFromSummary)
            ? copartnerFromSummary
            : undefined;
    if (
        copartnerIdInitial &&
        SERVER_ULID_RE.test(copartnerIdInitial) &&
        guestIds.has(copartnerIdInitial)
    ) {
        copartnerIdInitial = "";
    }
    return {
        firstName: p.firstName.trim(),
        lastName: p.lastName.trim(),
        email: p.email.trim(),
        phone: (p.phone ?? "").trim() || undefined,
        countryCode: mapCountryToCode(p.country),
        city: p.city?.trim() || undefined,
        role: mapRoleToApi(p.role),
        level: mapLevelToApi(p.level),
        lookingForPartner: lookingForPartnerForBookingApi(p),
        age: parseOptionalAge(p.age),
        gender: mapGenderToApi(p.gender),
        contactType: mapContactTypeToApi(p.contactType, p.instagram),
        contactValue: mapContactValueForApi(p),
        registrationMode: registrationModeForApi(p),
        copartnerId: copartnerIdInitial,
    };
}

/**
 * Тело POST `booking/participants` для черновика (`created` unpaid) и регистрации (`init`):
 * — id уже есть на сервере → `id` + только поля профиля;
 * — новый без `id` → только профиль; позиции корзины — в `POST …/booking/createBooking` (`items[]`).
 * Бронь задаётся только JWT (`eventBookingId` в токене), не полем тела.
 */
export function buildDraftBookingParticipantsUpsertBody(
    validParticipants: RegisteredParticipant[],
    summaryParticipants: AzDanceBookingSummaryParticipantDto[],
    jwtSub: string | null,
    _cart: CartItem[],
): { participants: Record<string, unknown>[] } {
    const serverIds = new Set(summaryParticipants.map((sp) => sp.id));

    const expandedParticipants = dedupeEphemeralRegisteredParticipantsForDraft(
        dedupeRegisteredParticipantsById(
            validParticipants.flatMap(expandChampionshipCopartnerForUpsert),
        ),
    );

    const guestIds = participantIdsInGuestMode(expandedParticipants);

    const participants = expandedParticipants.map((p, selfIndex) => {
        const summarySp = summaryParticipants.find((sp) => sp.id === p.id);
        const onServer =
            serverIds.has(p.id) ||
            (jwtSub != null && jwtSub !== "" && p.id === jwtSub);
        const base = mapParticipantToUpsertPersonal(p, summarySp, guestIds);
        const copartnerResolved = copartnerIdForDraftBatch(
            p,
            expandedParticipants,
            selfIndex,
            guestIds,
        );
        if (copartnerResolved !== undefined) {
            base.copartnerId = copartnerResolved;
        }
        if (onServer) {
            return {
                id: p.id,
                ...base,
            };
        }
        void _cart;
        return { ...base };
    });

    return { participants };
}
